// Copyright (c) 2021. Sendanor <info@sendanor.fi>. All rights reserved.

import * as React from "react";
import "./ConfigurePipelineRun.scss";
import FormAppClassName from "../../../../../constants/FormAppClassName";
import PipelineRunModel from "../../../../../nor/pipeline/types/PipelineRunModel";
import SelectField from "../../../../../nor/ui/components/fields/select/SelectField";
import {
    ChangeCallback
} from "../../../../../nor/ts/interfaces/callbacks";
import PipelineRunType from "../../../../../nor/pipeline/types/PipelineRunType";
import LogService from "../../../../../nor/ts/LogService";
import { SelectFieldItem } from "../../../../../nor/ui/types/items/SelectFieldModel";
import {
    find,
    first,
    has,
    isEqual,
    map
} from "../../../../../nor/ts/modules/lodash";
import RepositoryEntry from "../../../../../nor/ts/simpleRepository/types/RepositoryEntry";
import PipelineDTO from "../../../../../nor/pipeline/dto/PipelineDTO";
import AgentPoolDTO from "../../../../../nor/pipeline/dto/AgentPoolDTO";
import PipelineModel from "../../../../../nor/pipeline/types/PipelineModel";
import PipelineParameterArray from "../../../../../nor/pipeline/types/PipelineParameterArray";
import ParameterModel from "../../../../../nor/pipeline/parameters/ParameterModel";
import ParameterType from "../../../../../nor/pipeline/parameters/types/ParameterType";
import TextField from "../../../../../nor/ui/components/fields/text/TextField";
import ParameterBindingObject,
    {
    parseParameterBindingObjectMap
} from "../../../../../nor/pipeline/parameters/types/ParameterBindingObject";
import ParameterBindingMap from "../../../../../nor/pipeline/parameters/types/ParameterBindingMap";
import ParameterBindingString
    , { parseParameterBindingString } from "../../../../../nor/pipeline/parameters/types/ParameterBindingString";

const LOG = LogService.createLogger('ConfigurePipelineRun');

export interface ConfigurePipelineRunProps {

    readonly className     ?: string;
    readonly model         ?: PipelineRunModel;
    readonly agentList      : RepositoryEntry<AgentPoolDTO>[];
    readonly pipelineList   : RepositoryEntry<PipelineDTO>[];
    readonly change         : ChangeCallback<PipelineRunModel>;

}

export interface ConfigurePipelineRunState {

    readonly origBindings          : ParameterBindingMap<ParameterBindingString> | undefined;
    readonly bindings              : ParameterBindingMap<ParameterBindingObject> | undefined;

}

export class ConfigurePipelineRun
    extends React.Component<ConfigurePipelineRunProps, ConfigurePipelineRunState> {

    public static defaultProps: Partial<ConfigurePipelineRunProps> = {};


    private readonly _changePipelineRunCallback    : ChangeCallback<string>;
    private readonly _changeAgentPoolIdCallback    : ChangeCallback<string>;


    public constructor (props: ConfigurePipelineRunProps) {

        super(props);

        this.state = {
            origBindings           : undefined,
            bindings               : undefined
        };

        this._changePipelineRunCallback    = this._onChangePipelineId.bind(this);
        this._changeAgentPoolIdCallback    = this._onChangeAgentPoolId.bind(this);

    }

    public componentDidMount (): void {
        this._updateModel();
    }

    public componentDidUpdate (
        prevProps: Readonly<ConfigurePipelineRunProps>,
        prevState: Readonly<ConfigurePipelineRunState>,
        snapshot?: any
    ): void {

        if ( prevProps.model !== this.props.model ) {
            this._updateModel();
        }

    }

    public render () {

        const pipelineList : SelectFieldItem<string>[] = map(
            this.props?.pipelineList ?? [],
            (item : RepositoryEntry<PipelineDTO>) : SelectFieldItem<string> => {
                return {
                    label: `${item.data.model.name}`,
                    value: `${item.id}`
                };
            }
        );

        const agentPoolList : SelectFieldItem<string>[] = map(
            this.props?.agentList ?? [],
            (item : RepositoryEntry<AgentPoolDTO>) : SelectFieldItem<string> => {
                return {
                    label: `${ item?.data?.model?.name ?? item.id }`,
                    value: `${ item.id }`
                };
            }
        );

        const selectedPipelineId  = this.props?.model?.pipelineId;
        const selectedAgentPoolId = first(this.props?.model?.agentPoolIdList ?? []);

        const parameterList : PipelineParameterArray = this.props?.model?.model?.parameters ?? [];

        return (
            <div className={FormAppClassName.SELECT_PIPELINE_RUN + ' ' + (this.props.className ?? '')}>

                <SelectField
                    className={FormAppClassName.SELECT_PIPELINE_RUN + '-select-pipeline'}
                    label={'Select Pipeline'}
                    value={selectedPipelineId}
                    values={pipelineList}
                    change={this._changePipelineRunCallback}
                />

                <SelectField
                    className={FormAppClassName.SELECT_PIPELINE_RUN + '-select-agent-pool'}
                    label={'Select Agent Pool'}
                    value={selectedAgentPoolId}
                    values={agentPoolList}
                    change={this._changeAgentPoolIdCallback}
                />

                {parameterList.length ? (
                    <div className={FormAppClassName.SELECT_PIPELINE_RUN + '-select-parameters'}>

                        <h3 className={FormAppClassName.SELECT_PIPELINE_RUN + '-select-parameters-title'}>Parameters</h3>

                        {map(parameterList, (item : ParameterModel) : any => {

                            const type        = item.type;
                            const name        = item.name;
                            const displayName = item?.displayName ?? name;

                            const prevBindingValue : ParameterBindingString | undefined = this.state.origBindings && has(this.state.origBindings, name) ? this.state.origBindings[name] : '';
                            const bindingValue     : string = prevBindingValue ? prevBindingValue : '';

                            //const bindingObject : ParameterBindingObject | undefined = this.state.bindings && has(this.state.bindings, name) ? this.state.bindings[name] : undefined;

                            const changeBinding = (value: string | undefined) => {
                                const newValue : ParameterBindingString | undefined = parseParameterBindingString(value);
                                if (newValue !== prevBindingValue) {
                                    this._setParameterBinding(name, newValue);
                                }
                            };

                            return (
                                <div key={`parameter-${item.name}`}
                                     className={FormAppClassName.SELECT_PIPELINE_RUN + '-select-parameters-item'}
                                >
                                    {type === ParameterType.STRING ? (
                                        <TextField
                                            className={FormAppClassName.SELECT_PIPELINE_RUN + '-select-parameters-item-value'}
                                            label={displayName}
                                            value={bindingValue}
                                            change={changeBinding}
                                        />
                                    ) : null}

                                    {type === ParameterType.BOOLEAN ? (
                                        <TextField
                                            className={FormAppClassName.SELECT_PIPELINE_RUN + '-select-parameters-item-value'}
                                            label={displayName}
                                            value={bindingValue}
                                            change={changeBinding}
                                        />
                                    ) : null}

                                    {type === ParameterType.INTEGER ? (
                                        <TextField
                                            className={FormAppClassName.SELECT_PIPELINE_RUN + '-select-parameters-item-value'}
                                            label={displayName}
                                            value={bindingValue}
                                            change={changeBinding}
                                        />
                                    ) : null}

                                    {type === ParameterType.NUMBER ? (
                                        <TextField
                                            className={FormAppClassName.SELECT_PIPELINE_RUN + '-select-parameters-item-value'}
                                            label={displayName}
                                            value={bindingValue}
                                            change={changeBinding}
                                        />
                                    ) : null}

                                    {type === ParameterType.JSON ? (
                                        <TextField
                                            className={FormAppClassName.SELECT_PIPELINE_RUN + '-select-parameters-item-value'}
                                            label={displayName}
                                            value={bindingValue}
                                            change={changeBinding}
                                        />
                                    ) : null}

                                </div>
                            );
                        })}

                    </div>
                ) : null}

            </div>
        );

    }


    private _updateModel () {
        this._updateBindings();
    }

    private _updateBindings () {

        const currentBindings = this.props?.model?.bindings;
        if ( this.state.origBindings !== currentBindings ) {
            this.setState({
                origBindings: currentBindings,
                bindings: parseParameterBindingObjectMap(currentBindings)
            });
        }

    }

    private _findPipeline (id: string) : RepositoryEntry<PipelineDTO> | undefined {
        const list : RepositoryEntry<PipelineDTO>[] = this.props?.pipelineList || [];
        return find(list, (item : RepositoryEntry<PipelineDTO>) : boolean => item.id === id);
    }

    private _onChangePipelineId (pipelineId: string) {

        const pipeline : RepositoryEntry<PipelineDTO> | undefined = this._findPipeline(pipelineId);
        if (!pipeline) {
            LOG.error(`_onChangePipelineId: Could not find pipeline for: ` , pipelineId);
            return;
        }

        LOG.debug(`_onChangePipelineId: found: `, pipelineId, pipeline);

        const pipelineModel : PipelineModel = pipeline.data.model;

        const currentPipelineId = this.props?.model?.pipelineId;
        const currentModel = this.props?.model?.model;

        if ( currentModel !== pipelineModel || currentPipelineId !== pipelineId ) {

            const newModel : PipelineRunModel = {
                ...(this.props?.model ?? {
                    pipelineId,
                    type: PipelineRunType.ONCE,
                    agentPoolIdList: []
                }),
                pipelineId: pipelineId,
                model: pipelineModel
            };

            LOG.debug(`_onChangePipelineId: Selecting: `, pipelineId, newModel);

            this._updatePipelineRunModelToParent(newModel);

        } else {
            LOG.warn(`_onChangePipelineId: The model was not changed: `, currentModel, pipelineModel);
        }

    }

    private _onChangeAgentPoolId (poolId: string) {

        const pipelineId = this.props.model?.pipelineId ?? undefined;
        if (!pipelineId) {
            LOG.error(`Select pipeline first`);
            return;
        }

        const agentPoolIdList = [ poolId ];
        if ( !isEqual(this.props?.model?.agentPoolIdList, agentPoolIdList) ) {

            const newModel: PipelineRunModel = {
                ...(this.props?.model ?? {
                    pipelineId,
                    type: PipelineRunType.ONCE
                }),
                agentPoolIdList
            };

            this._updatePipelineRunModelToParent(newModel);

        } else {
            LOG.warn(`_onChangeAgentPoolId: The agentPoolIdList was not changed`)
        }

    }

    private _updatePipelineRunModelToParent (newModel: PipelineRunModel) {
        try {
            LOG.debug(`_updatePipelineRunModelToParent: Changing pipeline run as: `, newModel);
            this.props.change(newModel);
        } catch (err) {
            LOG.error(`_updatePipelineRunModelToParent: Error: `, err);
        }
    }

    private _setParameterBinding (
        name  : string,
        value : ParameterBindingString | undefined
    ) {

        const pipelineId = this.props.model?.pipelineId ?? undefined;
        if (!pipelineId) {
            LOG.error(`_setParameterBinding: Select pipeline first`);
            return;
        }

        const prevBindings : ParameterBindingMap<ParameterBindingString> | undefined = this.props?.model?.bindings;

        const newBindings  : ParameterBindingMap<ParameterBindingString> = {
            ...(prevBindings ?? {} as ParameterBindingMap<ParameterBindingString>),
            ...((value ? {[name] : value} : {}) as ParameterBindingMap<ParameterBindingString>)
        };

        if ( !isEqual(prevBindings, newBindings) ) {

            const newModel : PipelineRunModel = {
                ...(this.props?.model ?? {
                    pipelineId,
                    type: PipelineRunType.ONCE,
                    agentPoolIdList: []
                }),
                bindings: newBindings
            };

            this._updatePipelineRunModelToParent(newModel);

        } else {
            LOG.warn(`_setParameterBinding: The bindings was not changed`)
        }

    }

}

export default ConfigurePipelineRun;
